import { events, type EventItem } from '@/mocks/events';
import { categories, type CategoryItem } from '@/mocks/categories';

export function formatDate(date: string): string {
  const [y, m, d] = date.split('-');
  return `${y}年${Number(m)}月${Number(d)}日`;
}

export function formatViews(views: number): string {
  if (views >= 10000) {
    return `${(views / 10000).toFixed(1)}万`;
  }
  return `${views}`;
}

export function getCategoryById(id: string): CategoryItem | undefined {
  return categories.find((c) => c.id === id);
}

export function getCategoryName(id: string): string {
  if (id === 'all') return '全部赛事';
  return getCategoryById(id)?.name ?? '赛事资讯';
}

export function getEventById(id: string): EventItem | undefined {
  return events.find((e) => e.id === id);
}

/** 以数据集中最新日期作为“当前时间”基准，保证演示结果稳定 */
function getReferenceNow(): number {
  const latest = events.reduce((max, e) => (e.date > max ? e.date : max), '1970-01-01');
  return new Date(`${latest}T00:00:00`).getTime();
}

export function getFeaturedEvents(limit = 5): EventItem[] {
  const featured = events.filter((e) => e.featured);
  const pool = featured.length >= limit ? featured : [...featured, ...events];
  return pool.slice(0, limit);
}

export function getHotEvents(limit = 5): EventItem[] {
  return [...events].sort((a, b) => b.views - a.views).slice(0, limit);
}

export function getLatestEvents(limit = 8): EventItem[] {
  return [...events].sort((a, b) => (a.date < b.date ? 1 : -1)).slice(0, limit);
}

export function getRelatedEvents(current: EventItem, limit = 5): EventItem[] {
  const sameCategory = events
    .filter((e) => e.id !== current.id && e.categoryId === current.categoryId)
    .sort((a, b) => b.views - a.views);
  const others = events
    .filter((e) => e.id !== current.id && e.categoryId !== current.categoryId)
    .sort((a, b) => b.views - a.views);
  return [...sameCategory, ...others].slice(0, limit);
}

export interface FilterOptions {
  categoryId?: string;
  region?: string;
  timeRange?: string;
  sort?: string;
}

function withinTimeRange(date: string, timeRange?: string): boolean {
  if (!timeRange) return true;
  const now = getReferenceNow();
  const target = new Date(`${date}T00:00:00`).getTime();
  const diffDays = (now - target) / (1000 * 60 * 60 * 24);
  if (timeRange === '1m') return diffDays <= 31;
  if (timeRange === '3m') return diffDays <= 92;
  if (timeRange === '1y') return diffDays <= 366;
  return true;
}

export function filterEvents(options: FilterOptions): EventItem[] {
  const { categoryId, region, timeRange, sort } = options;
  let result = [...events];

  if (categoryId && categoryId !== 'all') {
    result = result.filter((e) => e.categoryId === categoryId);
  }
  if (region) {
    result = result.filter((e) => e.region === region);
  }
  if (timeRange) {
    result = result.filter((e) => withinTimeRange(e.date, timeRange));
  }

  if (sort === 'hot') {
    result.sort((a, b) => b.views - a.views);
  } else {
    result.sort((a, b) => (a.date < b.date ? 1 : -1));
  }
  return result;
}

export function searchEvents(keyword: string): EventItem[] {
  const q = keyword.trim().toLowerCase();
  if (!q) return [];
  return events
    .filter((e) => {
      const haystack = `${e.title} ${e.summary} ${e.categoryName} ${e.tags.join(' ')} ${e.intro}`.toLowerCase();
      return haystack.includes(q);
    })
    .sort((a, b) => (a.date < b.date ? 1 : -1));
}

export function paginate<T>(list: T[], page: number, pageSize = 12): T[] {
  const start = (page - 1) * pageSize;
  return list.slice(start, start + pageSize);
}

export function totalPages(total: number, pageSize = 12): number {
  return Math.max(1, Math.ceil(total / pageSize));
}